import { useState, useEffect } from 'react';
import { useAffiliateProfile, useUpdateProfile } from '../hooks/useAffiliate';
import { ShieldCheck, ArrowRight, Camera } from 'lucide-react';

export default function Profile() {
  const { data: profile, isLoading } = useAffiliateProfile();
  const updateProfileMutation = useUpdateProfile();

  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    displayName: '',
    bio: '',
    website: '',
    paymentPhone: '',
  });

  useEffect(() => {
    if (profile) {
      setFormData({
        displayName: profile.displayName || '',
        bio: profile.bio || '',
        website: profile.website || '',
        paymentPhone: profile.paymentPhone || '',
      });
    }
  }, [profile]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await updateProfileMutation.mutateAsync(formData);
      setIsEditing(false);
    } catch (error) {
      console.error('Failed to update profile:', error);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="w-8 h-8 border-[3px] border-zinc-100 border-t-emerald-600 rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!profile) return null;

  return (
    <div className="max-w-2xl mx-auto px-6 py-16 md:py-24 animate-in fade-in duration-700">
      {/* Luxurious Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-10 mb-16 px-2">
        <div className="flex items-center gap-8">
          <div className="relative group">
            <div className="absolute -inset-1 bg-gradient-to-tr from-emerald-500/20 to-zinc-500/0 rounded-full blur-md opacity-75 group-hover:opacity-100 transition duration-500"></div>
            <div className="relative w-20 h-20 rounded-full bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 flex items-center justify-center text-2xl font-light tracking-widest text-zinc-900 dark:text-zinc-100 shadow-sm overflow-hidden">
              <span className="z-10">{profile.user?.firstName?.charAt(0)}{profile.user?.lastName?.charAt(0)}</span>
              <div className="absolute inset-0 bg-zinc-50 dark:bg-zinc-800/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center cursor-pointer">
                <Camera className="w-5 h-5 text-zinc-400" />
              </div>
            </div>
            <div className="absolute bottom-0 right-0 w-6 h-6 bg-emerald-500 rounded-full border-4 border-white dark:border-zinc-950 flex items-center justify-center">
              <ShieldCheck className="w-3 h-3 text-white" />
            </div>
          </div>
          <div className="space-y-1.5">
            <h1 className="text-3xl font-bold tracking-tight text-zinc-900 dark:text-zinc-100 leading-none">
              {profile.displayName}
            </h1>
            <div className="flex items-center gap-2">
              <p className="text-zinc-400 text-sm font-medium">{profile.user?.email}</p>
              <span className="w-1 h-1 rounded-full bg-emerald-500/40"></span>
              <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-emerald-600/80">Affiliate Partner</span>
            </div>
          </div>
        </div>

        {!isEditing && (
          <button
            onClick={() => setIsEditing(true)}
            className="group flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] px-6 py-3 bg-zinc-900 dark:bg-zinc-100 text-white dark:text-zinc-900 rounded-full hover:shadow-xl hover:shadow-zinc-500/10 hover:-translate-y-0.5 transition-all active:scale-95"
          >
            Edit Profile
            <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
          </button>
        )}
      </div>

      {/* Refined Stats Grid */}
      <div className="grid grid-cols-3 gap-0 border-y border-zinc-100 dark:border-zinc-800 mb-16">
        <div className="py-10 pr-6 border-r border-zinc-100 dark:border-zinc-800">
          <p className="text-[10px] text-zinc-400 font-bold uppercase tracking-[0.2em] mb-3">Total Earnings</p>
          <div className="flex items-baseline gap-1">
            <span className="text-xs font-medium text-zinc-400">KES</span>
            <p className="text-2xl font-bold tracking-tight">
              {profile.totalEarnings.toLocaleString()}
            </p>
          </div>
        </div>
        <div className="py-10 px-6 border-r border-zinc-100 dark:border-zinc-800">
          <p className="text-[10px] text-zinc-400 font-bold uppercase tracking-[0.2em] mb-3">Total Sales</p>
          <p className="text-2xl font-bold tracking-tight">{profile.totalSales}</p>
        </div>
        <div className="py-10 pl-6 text-right">
          <p className="text-[10px] text-zinc-400 font-bold uppercase tracking-[0.2em] mb-3">Total Clicks</p>
          <p className="text-2xl font-bold tracking-tight">{profile.totalClicks}</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-16">
        <section className="space-y-10">
          <div className="flex items-center gap-4">
            <h2 className="text-[10px] font-bold text-zinc-400 uppercase tracking-[0.3em] whitespace-nowrap">Profile Information</h2>
            <div className="h-[1px] w-full bg-zinc-100 dark:bg-zinc-800"></div>
          </div>

          <div className="space-y-12">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-12 text-sm">
              <div className="space-y-3">
                <label className="text-[10px] font-bold text-zinc-400 uppercase tracking-[0.2em] ml-0.5">Display Name</label>
                {isEditing ? (
                  <input
                    value={formData.displayName}
                    onChange={(e) => setFormData({ ...formData, displayName: e.target.value })}
                    className="w-full pb-2 bg-transparent border-b border-zinc-200 dark:border-zinc-800 focus:border-zinc-900 dark:focus:border-zinc-100 outline-none transition-colors font-medium text-[15px]"
                  />
                ) : (
                  <p className="text-zinc-900 dark:text-zinc-100 font-medium text-[15px] pb-2 border-b border-transparent">{profile.displayName}</p>
                )}
              </div>

              <div className="space-y-3">
                <label className="text-[10px] font-bold text-zinc-400 uppercase tracking-[0.2em] ml-0.5">Website</label>
                {isEditing ? (
                  <input
                    value={formData.website}
                    onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                    className="w-full pb-2 bg-transparent border-b border-zinc-200 dark:border-zinc-800 focus:border-zinc-900 dark:focus:border-zinc-100 outline-none transition-colors font-medium text-[15px]"
                    placeholder="https://..."
                  />
                ) : (
                  <p className="text-zinc-900 dark:text-zinc-100 font-medium text-[15px] pb-2 border-b border-transparent truncate">
                    {profile.website || 'No website set'}
                  </p>
                )}
              </div>
            </div>

            <div className="space-y-3">
              <label className="text-[10px] font-bold text-zinc-400 uppercase tracking-[0.2em] ml-0.5">Bio</label>
              {isEditing ? (
                <textarea
                  value={formData.bio}
                  onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                  rows={4}
                  className="w-full p-4 bg-zinc-50 dark:bg-zinc-800/10 border border-zinc-200/50 dark:border-zinc-800 rounded-2xl focus:border-zinc-900 dark:focus:border-zinc-100 focus:ring-4 focus:ring-zinc-950/5 outline-none transition-all font-medium leading-relaxed resize-none"
                />
              ) : (
                <p className="text-zinc-500 dark:text-zinc-400 leading-relaxed font-medium bg-zinc-50/50 dark:bg-zinc-800/20 p-6 rounded-2xl border border-zinc-200 dark:border-zinc-800/50">
                  {profile.bio || 'Add a short bio...'}
                </p>
              )}
            </div>
          </div>
        </section>

        <section className="space-y-10">
          <div className="flex items-center gap-4">
            <h2 className="text-[10px] font-bold text-zinc-400 uppercase tracking-[0.3em] whitespace-nowrap">Payment Settings</h2>
            <div className="h-[1px] w-full bg-zinc-100 dark:bg-zinc-800"></div>
          </div>

          <div className="space-y-4 max-w-xs">
            <label className="text-[10px] font-bold text-zinc-400 uppercase tracking-[0.2em] ml-0.5">M-Pesa Number</label>
            {isEditing ? (
              <div className="flex items-center gap-3 pb-2 border-b border-zinc-200 dark:border-zinc-800">
                <span className="text-emerald-600 font-bold">+254</span>
                <input
                  value={formData.paymentPhone}
                  onChange={(e) => setFormData({ ...formData, paymentPhone: e.target.value })}
                  className="w-full bg-transparent focus:outline-none font-bold text-xl tracking-tighter text-emerald-600"
                  placeholder="7XXXXXXXX"
                />
              </div>
            ) : (
              <div className="flex items-center gap-3">
                <span className="text-zinc-300 font-medium">+254</span>
                <p className="font-bold text-2xl tracking-tighter text-zinc-900 dark:text-zinc-100">
                  {profile.paymentPhone || 'Not set'}
                </p>
              </div>
            )}
          </div>
        </section>

        {isEditing && (
          <div className="flex items-center gap-6 pt-10 sticky bottom-8 z-50">
            <button
              type="submit"
              disabled={updateProfileMutation.isPending}
              className="px-10 py-4 bg-zinc-900 dark:bg-zinc-100 text-white dark:text-zinc-900 rounded-full text-xs font-bold uppercase tracking-[0.2em] hover:shadow-2xl hover:-translate-y-1 transition-all active:scale-95 disabled:opacity-50"
            >
              {updateProfileMutation.isPending ? 'Saving...' : 'Save Changes'}
            </button>
            <button
              type="button"
              onClick={() => setIsEditing(false)}
              className="text-[10px] font-bold text-zinc-400 hover:text-red-500 uppercase tracking-[0.2em] transition-colors"
            >
              Cancel
            </button>
          </div>
        )}
      </form>
    </div>
  );
}
